import React from 'react';
import { motion } from 'framer-motion';
import { EnhancedInputField } from '../reusable/EnhancedInputField';
import ToggleSwitch from '../reusable/ToggleSwitch';
import FileInput from '../reusable/FileInput';
import EnhancedTextarea from '../reusable/EnhancedTextarea';
import PhoneInput from '../reusable/PhoneInput';
import CategoryItems from '../reusable/CategoryItems';
import InstructorItems from '../reusable/InstructorItems';
import StudentsCourseItems from '../reusable/StudentsCourseItems';
import UserItems from '../reusable/UserItems';
import CourseItems from '../reusable/CourseItems';
import Datepicker from 'react-tailwindcss-datepicker';
import dayjs from 'dayjs';
import Loader from '../Live/Loader';

const formVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

const fieldVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 }
};

/**
 * Shared form component for Create and Edit pages
 * Preserves all original form fields, validation, and behavior
 */
const SharedForm = ({
  fields,
  formData,
  handleChange,
  handleSubmit,
  loading,
  hasCategories,
  hasInstructors,
  hasStudents,
  hasUsers,
  hasCourses,
  entity,
  formErrors = {},
}) => {
  const categoryField = hasCategories ? fields.find(field => field.name === 'category_id') : null;
  const instructorField = hasInstructors ? fields.find(field => field.name === 'instructor_id') : null;
  const studentsField = hasStudents ? fields.find(field => field.name === 'student_id') : null;
  const usersField = hasUsers ? fields.find(field => field.name === 'user_uuid') : null;
  const courseField = hasCourses ? fields.find(field => field.name === 'course_id' && field.type === 'select') : null;

  // Separate fields into logical groups for better organization
  const textNumberFields = fields.filter(field =>
    field.type === 'text' ||
    field.type === 'number' ||
    field.type === 'password'
  );

  // Dropdown/select fields
  const selectFields = fields.filter(field =>
    field.type === 'select' &&
    field.name !== 'category_id' &&
    field.name !== 'instructor_id' &&
    field.name !== 'student_id' &&
    field.name !== 'user_uuid' &&
    field.name !== 'course_id'
  );

  // Other fields (textarea, file, toggle, datepicker, radio)
  const otherFields = fields.filter(field =>
    field.type !== 'text' &&
    field.type !== 'number' &&
    field.type !== 'password' &&
    field.type !== 'select' &&
    field.name !== 'student_id' &&
    field.name !== 'instructor_id' &&
    field.name !== 'category_id' &&
    field.name !== 'user_uuid' &&
    field.name !== 'course_id' &&
    field.name !== 'by_who'
  );


  // Set course title for payment when adding
  const courseObject = formData.course ?? null;
  const courseTitle = courseObject?.title;

  const renderSwitch = (field) => {
    switch (field.type) {
      case 'phone':
        return (
          <PhoneInput
            key={field.name}
            label={field.label}
            name={field.name}
            placeholder={field.placeholder}
            value={formData[field.name] || ''}
            handleChange={handleChange}
            required={field.required}
            disabled={field.disabled}
            error={formErrors[field.name]}
          />
        );
      case 'toggle':
        return (
          <ToggleSwitch
            key={field.name}
            label={field.label}
            checked={formData[field.name]}
            onChange={() => handleChange({
              target: { name: field.name, value: !formData[field.name] }
            })}
            className='space-x-2'
          />
        );
      case 'select':
        return (
          <EnhancedInputField
            key={field.name}
            name={field.name}
            type={field.type}
            handleChange={handleChange}
            label={field.label}
            placeholder={field.placeholder}
            value={formData[field.name]}
            options={field.options}
            error={formErrors[field.name]}
          />
        );
      case 'file':
        return (
          <div key={field.name} className="sm:col-span-2">
            <FileInput
              id={field.name}
              onChange={handleChange}
              oldValue={formData[field.name]}
            />
          </div>
        );
      case 'textarea':
        return (
          <EnhancedTextarea
            key={field.name}
            label={field.label}
            name={field.name}
            placeholder={field.placeholder}
            value={formData[field.name] || ''}
            handleChange={handleChange}
            error={formErrors[field.name]}
            rows={field.rows || 4}
            maxLength={field.maxLength}
          />
        );
      case 'radio':
        return (
          <div key={field.name} className="space-y-2">
            <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
              {field.label}
            </label>
            <div className="flex flex-wrap gap-4">
              {field.value.map((val) => (
                <div key={val} className="flex items-center gap-2">
                  <input
                    id={`${field.name}-${val}`}
                    name={field.name}
                    type="radio"
                    value={val}
                    checked={formData[field.name] === val}
                    onChange={handleChange}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                  <label
                    htmlFor={`${field.name}-${val}`}
                    className="text-sm text-gray-700 dark:text-gray-300 capitalize cursor-pointer"
                  >
                    {field.options?.find(opt => opt.value === val)?.label || val}
                  </label>
                </div>
              ))}
            </div>
            {formErrors[field.name] && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors[field.name]}</p>
            )}
          </div>
        );
      case 'datepicker': {
        const today = new Date();
        const oneDay = 24 * 60 * 60 * 1000;

        // Default constraints (very broad)
        let minDate = new Date(today.getFullYear() - 100, 0, 1);
        let maxDate = new Date(today.getFullYear() + 100, 11, 31);

        // Specific constraints for Birthday (used in student/instructor forms)
        if (field.name === 'birthday' || field.name === 'birth_date') {
          maxDate = new Date(today.getFullYear() - 6, today.getMonth(), today.getDate());
          minDate = new Date(today.getFullYear() - 60, today.getMonth(), today.getDate());
        }

        const savedDate = formData[field.name];
        const selectedDate = savedDate
          ? {
            startDate: new Date(savedDate),
            endDate: new Date(savedDate)
          }
          : { startDate: null, endDate: null };

        return (
          <div key={field.name} className="">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {field.label}
            </label>
            <Datepicker
              inputClassName="rounded-lg border-2 w-full p-2 bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              readOnly={true}
              asSingle={true}
              useRange={false}
              key={field.name}
              primaryColor='blue'
              value={selectedDate}
              placeholder={field.placeholder}
              onChange={(newValue) => {
                handleChange({
                  target: {
                    name: field.name,
                    value: dayjs(newValue.startDate).format("YYYY-MM-DD")
                  }
                });
              }}
              popoverDirection="up"
              startWeekOn="sat"
              toggleClassName="absolute bg-blue-600 rounded-r-lg text-white right-0 h-full px-3 text-gray-400 focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed"
              disabledDates={[
                {
                  startDate: new Date(1900, 0, 1),
                  endDate: new Date(minDate.getTime() - oneDay),
                },
                {
                  startDate: new Date(maxDate.getTime() + oneDay),
                  endDate: new Date(2100, 11, 31),
                }
              ]}
            />

            {formErrors[field.name] && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors[field.name]}</p>
            )}
          </div>
        );
      }
      default:
        return null;
    }
  };

  return (
    <motion.form
      onSubmit={handleSubmit}
      initial="hidden"
      animate="visible"
      variants={formVariants}
      transition={{ duration: 0.3 }}
      className="w-full relative"
      style={{ overflow: 'visible' }}
    >
      <div className="grid gap-6 grid-cols-1 sm:grid-cols-2">
        {/* Render text and number fields first */}
        {textNumberFields.map((field, index) => (
          <motion.div
            key={field.name}
            variants={fieldVariants}
            transition={{ delay: index * 0.05 }}
            className="relative"
          >
            <EnhancedInputField
              {...field}
              label={field.label}
              name={field.name}
              type={field.type}
              placeholder={field.placeholder}
              value={field.name === 'course_id' && (entity === 'lesson') ? courseTitle : (formData[field.name] || '')}
              handleChange={handleChange}
              options={field.options}
              disabled={field.name === 'course_id'}
              error={formErrors[field.name]}
            />
          </motion.div>
        ))}

        {/* Render dropdown/select fields early in the form (before other fields) */}
        {hasCategories && categoryField && (
          <motion.div
            variants={fieldVariants}
            transition={{ delay: textNumberFields.length * 0.05 }}
            className="relative"
          >
            <CategoryItems field={categoryField} formData={formData} handleChange={handleChange} type="form" />
            {formErrors.category_id && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.category_id}</p>
            )}
          </motion.div>
        )}

        {hasInstructors && instructorField && (
          <motion.div
            variants={fieldVariants}
            transition={{ delay: (textNumberFields.length + (hasCategories ? 1 : 0)) * 0.05 }}
            className="relative"
          >
            <InstructorItems field={instructorField} formData={formData} handleChange={handleChange} type="form" />
            {formErrors.instructor_id && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.instructor_id}</p>
            )}
          </motion.div>
        )}

        {hasStudents && studentsField && (
          <motion.div
            variants={fieldVariants}
            transition={{ delay: (textNumberFields.length + (hasCategories ? 1 : 0) + (hasInstructors ? 1 : 0)) * 0.05 }}
            className="relative"
          >
            <StudentsCourseItems field={studentsField} formData={formData} handleChange={handleChange} type="form" />
            {formErrors.student_id && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.student_id}</p>
            )}
          </motion.div>
        )}

        {hasUsers && usersField && (
          <motion.div
            variants={fieldVariants}
            transition={{ delay: (textNumberFields.length + (hasCategories ? 1 : 0) + (hasInstructors ? 1 : 0) + (hasStudents ? 1 : 0)) * 0.05 }}
            className="relative"
          >
            <UserItems field={usersField} formData={formData} handleChange={handleChange} type="form" accountType={entity} />
            {formErrors.user_uuid && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.user_uuid}</p>
            )}
          </motion.div>
        )}

        {/* Render dropdown/select fields */}
        {selectFields.map((field, index) => (
          <motion.div
            key={field.name}
            variants={fieldVariants}
            transition={{ delay: (textNumberFields.length + index) * 0.05 }}
            className="relative"
          >
            <EnhancedInputField
              {...field}
              label={field.label}
              name={field.name}
              type={field.type}
              placeholder={field.placeholder}
              value={formData[field.name] || ''}
              handleChange={handleChange}
              options={field.options}
              error={formErrors[field.name]}
            />
          </motion.div>
        ))}

        {hasCourses && courseField && (
          <motion.div
            variants={fieldVariants}
            transition={{ delay: (textNumberFields.length + selectFields.length + (hasCategories ? 1 : 0) + (hasInstructors ? 1 : 0) + (hasStudents ? 1 : 0) + (hasUsers ? 1 : 0)) * 0.05 }}
            className="relative"
          >
            <CourseItems field={courseField} formData={formData} handleChange={handleChange} type="form" />
            {formErrors.course_id && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.course_id}</p>
            )}
          </motion.div>
        )}

        {/* Render other fields (textarea, file, toggle, datepicker, radio) */}

        {otherFields.map((field, index) => {
          const delayIndex = textNumberFields.length +
            selectFields.length +
            (hasCategories ? 1 : 0) +
            (hasInstructors ? 1 : 0) +
            (hasStudents ? 1 : 0) +
            (hasUsers ? 1 : 0) +
            (hasCourses ? 1 : 0) +
            index;


          return (
            <motion.div
              key={field.name}
              variants={fieldVariants}
              transition={{ delay: delayIndex * 0.05 }}
              className={field.type === 'textarea' || field.type === 'file' || field.type === 'datepicker' || field.type === 'toggle' ? 'sm:col-span-2' : 'sm:col-span-1'}
            >
              {renderSwitch(field)}
            </motion.div>
          );
        })}
      </div>

      <motion.div
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
        className="flex flex-col sm:flex-row justify-end gap-4 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700"
      >
        <motion.button
          type="submit"
          className="w-full sm:w-auto px-8 py-3.5 text-base font-semibold bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl disabled:shadow-none"
          disabled={loading}
          whileHover={!loading ? { scale: 1.02 } : {}}
          whileTap={!loading ? { scale: 0.98 } : {}}
        >
          {loading ? (
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 1 }}
              className="flex items-center justify-center gap-2"
            >
              <Loader />
              <span>Saving...</span>
            </motion.div>
          ) : (
            <span className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Save
            </span>
          )}
        </motion.button>
      </motion.div>
    </motion.form>
  );
};

export default SharedForm;
